<template>
    <div class="audio-video-player">
        <video ref="playerElement" class="video-js" />
        <div v-if="hasError" class="media-error">
            <p>Filen kan inte spelas upp. Den kan vara skadad.</p>
        </div>
    </div>
</template>

<script setup lang="ts">
    import videoJs from "video.js";
    import Player from "video.js/dist/types/player";
    import { onMounted, onUnmounted, ref, toRefs } from "vue";

    import("video.js/dist/video-js.css");

    const props = withDefaults(
        defineProps<{
            autoplay?: boolean;
            controls?: boolean;
            contentType: string;
            sources: Array<{
                src: string;
                type: string;
            }>;
        }>(),
        {
            autoplay: false,
            controls: true,
        }
    );

    const playerElement = ref<HTMLVideoElement | null>(null);
    const player = ref<Player | null>(null);
    const hasError = ref(false);
    const { autoplay, controls, sources } = toRefs(props);

    const handlePlayerError = () => {
        hasError.value = true;
        try {
            player.value?.pause();
        } catch (error) {
            console.error("Audio/video player pause failed", error);
        }
    };

    onMounted(() => {
        if (playerElement.value) {
            player.value = videoJs(playerElement.value, {
                autoplay: autoplay.value,
                controls: controls.value,
                sources: sources.value,
            });
            player.value.on("error", handlePlayerError);
        }
    });

    onUnmounted(() => {
        player.value?.off("error", handlePlayerError);
        player.value?.dispose();
    });
</script>

<style lang="scss" scoped>
    .audio-video-player {
        position: relative;
        width: 100%;
        height: 100%;
    }

    .media-error {
        position: absolute;
        inset: 0;
        display: grid;
        place-items: center;
        text-align: center;
        padding: 1rem;
        background: rgba(0, 0, 0, 0.65);
        color: #fff;
        z-index: 2;
    }
</style>
